import { Dropdown, Form, Input, Spin, Upload, Image, Breadcrumb } from "antd";
import React, { useEffect, useState } from "react";
import columns from "../../columns/documents/doc-folder.js";
import SkeletonTable from "../../components/table/SkeletonTable";
import {
  GetChildFolderListApi,
  GetFolderListApi,
  DeleteFileFolderApi,
  UploadFileFolderApi,
  MoveFileFolderApi,
  DownloadFileFolderApi,
} from "../../api/request/documents/documents.js";
import moment from "moment";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import Preview from "./components/Preview.jsx";
import ConfirmationModal from "../../components/modals/ConfirmationModal.jsx";
import LoadableButton from "../../components/button/LoadableButton.jsx";
import ModalComponent from "../../components/modals/ModalComponent.jsx";
import Add from "../../assets/Add.jsx";
import Delete from "../../assets/Delete.jsx";
import UploadFile from "../../assets/UploadFile.jsx";
import { saveAs } from "file-saver";

const Documents = () => {
  const [createFolderForm] = Form.useForm();
  const [uploadFileForm] = Form.useForm();
  const navigate = useNavigate();
  const [isFolderListLoading, setIsFolderListLoading] = useState(false);
  const [isChildFolderListLoading, setIsChildFolderListLoading] =
    useState(false);
  const [folders, setFolders] = useState([]);
  const [activeParentFolder, setActiveParentFolder] = useState(null);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [data, setData] = useState([]);
  const [selectedPreview, setSelectedPreview] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [isCreateFolderModalOpen, setIsCreateFolderModalOpen] = useState(false);
  const [isCreateFolderLoading, setIsCreateFolderLoading] = useState(false);
  const [isOpenUploadModal, setIsOpenUploadModal] = useState(false);
  const [isUploadFileLoading, setIsUploadFileLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [fileList, setFileList] = useState([]);
  const [breadcrumbItems, setBreadcrumbItems] = useState([]);
  const [isOpenMoveModal, setIsOpenMoveModal] = useState(false);
  const [isMoveFolderLoading, setIsMoveFolderLoading] = useState(false);
  const [isMoveFolderListLoading, setIsMoveFolderListLoading] = useState(false);
  const [moveBreadcrumbItems, setMoveBreadcrumbItems] = useState([]);
  const [folderListForMove, setFolderListForMove] = useState([]);
  const [folderForMove, setFolderForMove] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isShowUploadBtn, setIsShowUploadBtn] = useState(false);
  const [isFileError, setIsFileError] = useState(false);
  const [errorMessage, setErrorMessage] = useState([]);

  const MAX_FILE_SIZE = 20;

  const items = (item, is_create) => {
    const menuItems = [
      {
        label: (
          <div
            className="flex items-center cursor-pointer gap-3"
            onClick={() => downloadFileFolderManager(item)}
          >
            <img
              src="/assets/icons/download.svg"
              className="size-4"
              alt="icon"
            />
            <p>Download</p>
          </div>
        ),
        key: "1",
      },
    ];

    if (is_create === 1) {
      menuItems.unshift({
        label: (
          <div
            className="flex items-center cursor-pointer gap-3"
            onClick={() => setIsOpenMoveModal(true)}
          >
            <img
              src="/assets/icons/arrows-pointing-out.svg"
              className="size-4 rotate-45"
              alt="icon"
            />
            <p>Move</p>
          </div>
        ),
        key: "0",
      });

      menuItems.push({
        label: (
          <div
            className="flex items-center cursor-pointer gap-3 text-red-500"
            onClick={() => setIsDeleteModalOpen(true)}
          >
            <Delete className="!size-4" />
            <p>Delete</p>
          </div>
        ),
        key: "2",
      });
    }

    return menuItems;
  };

  const allowedTypes = [
    "image/jpeg",
    "image/png",
    "text/plain",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
    "application/vnd.ms-excel", // .xls
    "text/csv",
    "application/msword", // .doc
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx
    "application/pdf",
  ];

  const beforeUpload = (file) => {
    const isAllowedType = allowedTypes.includes(file.type);
    const isAllowedSize = file.size / 1024 / 1024 <= MAX_FILE_SIZE;

    const error = ["Please upload a valid document."];

    if (!isAllowedType) {
      error.push(
        "Allowed file types: .docx, .xls, .txt, .jpg, .jpeg, .png, .pdf."
      );
    }

    if (!isAllowedSize) {
      error.push(`Maximum file size: ${MAX_FILE_SIZE}MB.`);
    }

    if (!isAllowedType || !isAllowedSize) {
      setErrorMessage(error);
      setIsFileError(true);
      return Upload.LIST_IGNORE;
    }

    setIsFileError(false);
    return false;
  };

  const getBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });

  const handlePreview = async (file) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj);
    }
    setPreviewImage(file.url || file.preview);
    setPreviewOpen(true);
  };

  const handleChange = ({ fileList: newFileList }) => {
    setFileList(newFileList);
    uploadFileForm.setFieldsValue({ files: newFileList });
    if (newFileList?.length == 0) {
      uploadFileForm.setFieldsValue({ files: [] });
    }
  };

  const UploadButton = (
    <button type="button" className="flex flex-col items-center gap-2">
      <div className="size-7">
        <img src="/assets/icons/upload.svg" alt="icon" />
      </div>
      Upload
    </button>
  );

  const getFolderList = async () => {
    try {
      setIsFolderListLoading(true);
      const { data } = await GetFolderListApi();
      if (data?.status) {
        setFolders(data?.data);
        setFolderListForMove(
          data?.data?.map((item) => {
            return {
              ...item,
              name: (
                <div
                  className="flex items-center gap-3 cursor-pointer"
                  onClick={() => openMoveFolder(item)}
                >
                  <img
                    src="/assets/icons/folder.svg"
                    alt="icon"
                    className="size-5"
                  />
                  <p>{item?.original_name}</p>
                </div>
              ),
              addedDate: item?.created_at
                ? moment(item?.created_at).format("lll")
                : "--",
            };
          })
        );
        const selectedYear = data?.data?.find(
          (item) => item?.original_name === moment().year().toString()
        );
        if (selectedYear) handleFolderOpen(selectedYear);
      }
      setIsFolderListLoading(false);
    } catch (error) {
      setIsFolderListLoading(false);
    }
  };

  useEffect(() => {
    getFolderList();
  }, []);

  useEffect(() => {
    if (!isOpenMoveModal) {
      setFolderListForMove(
        folders?.map((item) => {
          return {
            ...item,
            name: (
              <div
                className="flex items-center gap-3 cursor-pointer"
                onClick={() => openMoveFolder(item)}
              >
                <img
                  src="/assets/icons/folder-red.svg"
                  alt="icon"
                  className="size-5"
                />
                <p>{item?.original_name}</p>
              </div>
            ),
            addedDate: item?.created_at
              ? moment(item?.created_at).format("lll")
              : "--",
          };
        })
      );
      setFolderForMove(null);
      setMoveBreadcrumbItems([]);
    }
  }, [isOpenMoveModal]);

  const getIcons = (file) => {
    const fileExtension = file.split(".").pop().toLowerCase() || null;

    switch (fileExtension) {
      case "pdf":
        return (
          <img
            src="/assets/icons/pdf-template.svg"
            alt="icon"
            className="size-7"
          />
        );
      case "jpeg":
      case "jpg":
      case "png":
        return (
          <img
            src="/assets/icons/img-template.svg"
            alt="icon"
            className="size-7"
          />
        );
      case "doc":
      case "docx":
        return (
          <img
            src="/assets/icons/doc-template.svg"
            alt="icon"
            className="size-7"
          />
        );
      case "csv":
        return (
          <img
            src="/assets/icons/csv-template.svg"
            alt="icon"
            className="size-7"
          />
        );
      case "txt":
        return (
          <img
            src="/assets/icons/txt-template.svg"
            alt="icon"
            className="size-7"
          />
        );
      case "xlsx":
        return (
          <img
            src="/assets/icons/excel-template.svg"
            alt="icon"
            className="w-6 h-7"
          />
        );
      default:
        return (
          <img src="/assets/icons/folder.svg" alt="icon" className="size-5" />
        );
    }
  };

  const openMoveFolder = async (folder) => {
    try {
      setIsMoveFolderListLoading(true);
      setFolderForMove(folder);
      const { data } = await GetChildFolderListApi({ folder_id: folder?.id });
      setMoveBreadcrumbItems(
        data?.breadcrumb
          ?.map((item) => {
            return {
              title: (
                <p
                  className="cursor-pointer"
                  onClick={() => openMoveFolder({ id: item?.id })}
                >
                  {item?.name}
                </p>
              ),
            };
          })
          ?.filter((item) => item !== null)
      );
      setFolderListForMove(
        data?.data
          ?.map((item) => {
            if (item?.type == "folder") {
              return {
                ...item,
                name: (
                  <div
                    className="flex items-center gap-3 cursor-pointer"
                    onClick={() => openMoveFolder(item)}
                  >
                    <img
                      src="/assets/icons/folder.svg"
                      alt="icon"
                      className="size-5"
                    />
                    <p>{item?.original_name}</p>
                  </div>
                ),
                addedDate: item?.created_at
                  ? moment(item?.created_at).format("lll")
                  : "--",
              };
            }
            return null;
          })
          ?.filter((item) => item !== null)
      );
      setIsMoveFolderListLoading(false);
    } catch (error) {
      setIsMoveFolderListLoading(false);
    }
  };

  const downloadFileFolderManager = async (item) => {
    try {
      setIsDownloading(true);
      let params = { doc_id: item?.id };
      const headers = { responseType: "arraybuffer" };
      const { data } = await DownloadFileFolderApi(params, headers);
      const blob = new Blob([data]);
      const fileName = item?.type === "file" ? item?.name : `${item?.name}.zip`;
      saveAs(blob, fileName);
      setSelectedItem("");
    } catch (error) {
      if (error?.response?.data?.error)
        toast.error(error?.response?.data?.error);
    } finally {
      setIsDownloading(false);
    }
  };

  const openFolder = async (folder) => {
    try {
      setIsChildFolderListLoading(true);
      setCurrentFolder(folder);
      const { data } = await GetChildFolderListApi({ folder_id: folder?.id });
      if (data?.status) {
        setIsShowUploadBtn(data?.is_create);
        setBreadcrumbItems(
          data?.breadcrumb
            ?.map((item) => {
              return {
                title: (
                  <p
                    className="cursor-pointer"
                    onClick={() => openFolder({ id: item?.id })}
                  >
                    {item?.name}
                  </p>
                ),
              };
            })
            ?.filter((item) => item !== null)
        );

        setData(
          data?.data?.map((item) => {
            return {
              ...item,
              name: (
                <div
                  className="flex items-center gap-3 cursor-pointer"
                  onClick={() =>
                    item?.type == "folder"
                      ? openFolder(item)
                      : setSelectedPreview(item)
                  }
                >
                  {getIcons(item?.original_name)}
                  <p>{item?.original_name}</p>
                </div>
              ),
              addedDate: item?.created_at
                ? moment(item?.created_at).format("lll")
                : "--",
              actions: (
                <Dropdown
                  menu={{ items: items(item, data?.is_create) }}
                  trigger={["click"]}
                  className="cursor-pointer size-6 min-h-6 min-w-6"
                >
                  <img
                    src="/assets/icons/ellipsis-vertical.svg"
                    alt="icon"
                    onClick={() => setSelectedItem(item)}
                  />
                </Dropdown>
              ),
            };
          })
        );
      }
      setIsChildFolderListLoading(false);
    } catch (error) {
      setIsChildFolderListLoading(false);
    }
  };

  const handleFolderOpen = (folder) => {
    if (folder?.id === activeParentFolder?.id) return;
    setActiveParentFolder(folder);
    openFolder(folder);
  };

  const deleteItemHandler = async () => {
    try {
      setIsDeleteLoading(true);
      const { data } = await DeleteFileFolderApi({ id: selectedItem?.id });
      if (data?.status) {
        setData((prevData) =>
          prevData.filter((item) => item.id !== selectedItem?.id)
        );
        setSelectedItem("");
        setIsDeleteModalOpen(false);
        toast.success(data?.message);
      }
      setIsDeleteLoading(false);
    } catch (error) {
      setIsDeleteLoading(false);
    }
  };

  const createNewFolder = async (values) => {
    if (!currentFolder) {
      toast.error("Please select folder first to create new folder.");
      return;
    }
    try {
      setIsCreateFolderLoading(true);
      let params = {
        ...values,
        parent_id: currentFolder?.id,
      };
      const { data } = await UploadFileFolderApi(params);
      if (data?.status) {
        toast.success(data?.message);
        openFolder(currentFolder);
        createFolderForm.resetFields();
        setIsCreateFolderModalOpen(false);
      }
      setIsCreateFolderLoading(false);
    } catch (error) {
      if (error?.response?.data?.message) {
        toast.error(error?.response?.data?.message);
      }
      setIsCreateFolderLoading(false);
    }
  };

  useEffect(() => {
    createFolderForm.resetFields();
  }, [isCreateFolderModalOpen]);

  const uploadFileHandler = async (values) => {
    const extractOriginFileObjs = (fileArray) => {
      return fileArray.map((file) => file.originFileObj);
    };
    try {
      if (!currentFolder) {
        toast.error("Please select folder first to upload.");
        return;
      }
      setIsUploadFileLoading(true);
      let params = {
        files: extractOriginFileObjs(values?.files),
        parent_id: currentFolder?.id,
      };

      let apiHeader = {
        headers: {
          Accept: "application/json",
          "Content-Type": "multipart/form-data",
        },
      };
      const { data } = await UploadFileFolderApi(params, apiHeader);
      if (data?.status) {
        toast.success(data?.message);
        openFolder(currentFolder);
        uploadFileForm.resetFields();
        setIsOpenUploadModal(false);
      }
      setIsUploadFileLoading(false);
    } catch (error) {
      if (error?.response?.data?.message) {
        toast.error(error?.response?.data?.message);
      }
      setIsUploadFileLoading(false);
    }
  };

  useEffect(() => {
    setFileList([]);
    uploadFileForm.resetFields();
  }, [isOpenUploadModal]);

  const handleMove = async () => {
    try {
      setIsMoveFolderLoading(true);
      let params = {
        from_id: selectedItem?.id,
        to_id: folderForMove?.id,
      };
      const { data } = await MoveFileFolderApi(params);
      if (data?.status) {
        toast.success(data?.message);
        setData((prevData) =>
          prevData.filter((item) => item.id !== selectedItem?.id)
        );
        setSelectedItem("");
        setIsOpenMoveModal(false);
      }
      setIsMoveFolderLoading(false);
    } catch (error) {
      setIsMoveFolderLoading(false);
    }
  };

  return (
    <div>
      {selectedPreview ? (
        <Preview
          selectedPreview={selectedPreview}
          setSelectedPreview={setSelectedPreview}
        />
      ) : (
        <div>
          <div className="grid xl:grid-cols-4 md:grid-cols-2 gap-4 mt-1">
            <div></div>
            <div className="xl:col-span-3 flex items-center justify-end">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  className="btn-outlined bg-white flex items-center gap-2 !px-4"
                  onClick={() => navigate("/documents_trash")}
                >
                  <img
                    src="/assets/icons/delete.svg"
                    alt="icon"
                    className="size-4"
                  />
                  Trash
                </button>
                <Input
                  size="large"
                  placeholder="Search here..."
                  className="xl:w-80"
                  prefix={
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="#B3B3B3"
                      className="size-6"
                      strokeWidth="5"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10.5 3.75a6.75 6.75 0 1 0 0 13.5 6.75 6.75 0 0 0 0-13.5ZM2.25 10.5a8.25 8.25 0 1 1 14.59 5.28l4.69 4.69a.75.75 0 1 1-1.06 1.06l-4.69-4.69A8.25 8.25 0 0 1 2.25 10.5Z"
                        clipRule="evenodd"
                      />
                    </svg>
                  }
                />
              </div>
            </div>
          </div>

          <div className="grid xl:grid-cols-4 md:grid-cols-2 gap-4 mt-6">
            <div className="border rounded-lg">
              <div className="border-b p-3 bg-white rounded-t-lg">
                <p className="font-semibold ">My Folders</p>
              </div>
              <div className="px-3 bg-white relative h-[calc(100vh-230px)] overflow-y-auto">
                {isFolderListLoading ? (
                  <div className="flex flex-col items-center gap-2 mt-16">
                    <Spin />
                    <p>Loading...</p>
                  </div>
                ) : (
                  <div>
                    {folders?.length > 0 ? (
                      folders?.map((folder, index) => (
                        <div
                          key={index}
                          onClick={() => handleFolderOpen(folder)}
                          className={`flex items-center gap-3 cursor-pointer my-3 px-3 py-2 rounded-md duration-300 hover:bg-secondaryLight ${
                            activeParentFolder?.original_name ==
                            folder?.original_name
                              ? "bg-secondaryLight"
                              : null
                          }`}
                        >
                          <img
                            src="/assets/icons/folder.svg"
                            className="size-5"
                          />
                          <p className="font-medium">{folder?.original_name}</p>
                        </div>
                      ))
                    ) : (
                      <p className="text-center mt-12">No Data</p>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="xl:col-span-3 border rounded-lg relative bg-white">
              {breadcrumbItems?.length > 0 && (
                <div className="flex justify-between items-center m-2">
                  <Breadcrumb
                    className="my-3 mx-4"
                    separator={
                      <div className="h-full flex items-center justify-center">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="var(--text-color)"
                          className="size-4"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.28 11.47a.75.75 0 0 1 0 1.06l-7.5 7.5a.75.75 0 0 1-1.06-1.06L14.69 12 7.72 5.03a.75.75 0 0 1 1.06-1.06l7.5 7.5Z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                    }
                    items={breadcrumbItems}
                  />
                  {isShowUploadBtn ? (
                    <div className="flex items-center gap-3">
                      <button
                        className="btn-primary !px-4 flex items-center gap-2"
                        onClick={() => setIsCreateFolderModalOpen(true)}
                      >
                        <Add />
                        Create Folder
                      </button>
                      <button
                        onClick={() => setIsOpenUploadModal(true)}
                        className="btn-primary !px-4 flex items-center gap-2"
                      >
                        <UploadFile />
                        Upload File
                      </button>
                    </div>
                  ) : null}
                </div>
              )}
              <div
                className={`${
                  breadcrumbItems?.length > 0 ? "table-radius-0" : ""
                } b-`}
              >
                <SkeletonTable
                  columns={columns}
                  data={data}
                  isLoading={isChildFolderListLoading || isDownloading}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* --- Delete confirmation modal --- */}
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        setIsOpen={setIsDeleteModalOpen}
        message={`Are you sure you want to delete this ${
          selectedItem?.type == "folder" ? "folder" : "file"
        }?`}
        onConfirm={deleteItemHandler}
        isLoading={isDeleteLoading}
        loadingLabel={`Deleting ${
          selectedItem?.type == "folder" ? "folder" : "file"
        }...`}
      />

      {/* --- Create new folder modal --- */}
      <ModalComponent
        isOpen={isCreateFolderModalOpen}
        setIsOpen={setIsCreateFolderModalOpen}
        title="Create new folder"
      >
        <Form
          className="w-full"
          autoComplete="off"
          form={createFolderForm}
          initialValues={{ name: "" }}
          onFinish={createNewFolder}
        >
          <div className="input-white">
            <Form.Item
              name="name"
              label="Folder name"
              className="mb-0"
              rules={[{ required: true, message: "Please enter folder name." }]}
            >
              <Input
                placeholder="Folder name"
                className="mt-1 w-full ps-4 py-2"
              />
            </Form.Item>
          </div>
          <LoadableButton
            className="mt-6"
            type="submit"
            lable="Save"
            isLoading={isCreateFolderLoading}
            loadingLable="Creating folder..."
          />
        </Form>
      </ModalComponent>

      {/* --- UPload file modal --- */}
      <ModalComponent
        isOpen={isOpenUploadModal}
        setIsOpen={setIsOpenUploadModal}
        // title="Upload Your Documents"
        width={600}
      >
        <div className="text-center text-Gray mb-5">
          <p className="text-xl text-[#2F2B3D] font-semibold mb-1">
            Upload Your Documents
          </p>
          <p>Allowed file types: .docx, .xls, .txt, .jpg, .jpeg, .png, .pdf.</p>
          <p>Maximum file size: ${MAX_FILE_SIZE}MB.</p>
        </div>

        <Form
          className="w-full"
          autoComplete="off"
          form={uploadFileForm}
          initialValues={{ files: [] }}
          onFinish={uploadFileHandler}
        >
          <div>
            <Form.Item
              name="files"
              rules={[{ required: true, message: "Please upload file." }]}
            >
              <Upload
                beforeUpload={beforeUpload}
                multiple
                listType="picture-card"
                fileList={fileList}
                onPreview={handlePreview}
                onChange={handleChange}
              >
                {fileList.length >= 5 ? null : UploadButton}
              </Upload>
            </Form.Item>
            {isFileError && errorMessage?.length ? (
              <div className="mt-5">
                {errorMessage?.map((error, index) => (
                  <p key={index} className="text-red-500 ">
                    {error}
                  </p>
                ))}
              </div>
            ) : null}
            {previewImage && (
              <Image
                wrapperStyle={{ display: "none" }}
                preview={{
                  visible: previewOpen,
                  onVisibleChange: (visible) => setPreviewOpen(visible),
                  afterOpenChange: (visible) => !visible && setPreviewImage(""),
                }}
                src={previewImage}
              />
            )}
          </div>

          <div className="mt-5">
            <LoadableButton
              type="submit"
              lable="Upload"
              isLoading={isUploadFileLoading}
              loadingLable="Uploading file..."
            />
          </div>
        </Form>
      </ModalComponent>

      {/* --- Move file modal --- */}
      <ModalComponent
        isOpen={isOpenMoveModal}
        setIsOpen={setIsOpenMoveModal}
        width={800}
      >
        <div
          className={`${breadcrumbItems?.length > 0 ? "table-radius-0" : ""}`}
        >
          <div className="xl:col-span-3 border rounded-lg relative">
            {moveBreadcrumbItems?.length > 0 && (
              <Breadcrumb
                className="my-3 mx-4"
                separator={
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="var(--text-color)"
                    className="size-4 mt-1"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.28 11.47a.75.75 0 0 1 0 1.06l-7.5 7.5a.75.75 0 0 1-1.06-1.06L14.69 12 7.72 5.03a.75.75 0 0 1 1.06-1.06l7.5 7.5Z"
                      clipRule="evenodd"
                    />
                  </svg>
                }
                items={moveBreadcrumbItems}
              />
            )}
            <div
              className={`${
                breadcrumbItems?.length > 0 ? "table-radius-0" : ""
              }`}
            >
              <SkeletonTable
                columns={columns.slice(0, 3)}
                data={folderListForMove}
                isLoading={isMoveFolderListLoading}
              />
            </div>
          </div>
          <div className="flex justify-end">
            {folderForMove && (
              <LoadableButton
                className="mt-4"
                type="button"
                lable="Move here"
                onClick={() => handleMove()}
                isLoading={isMoveFolderLoading}
                loadingLable={`Moving ${
                  selectedItem?.type == "folder" ? "folder" : "file"
                }...`}
              />
            )}
          </div>
        </div>
      </ModalComponent>
    </div>
  );
};

export default Documents;
